import { SlingoModel } from "@lib/titan2-slingo/dist/game/model/SlingoModel";
/**
 * GameModel.ts
 */
export class GameModel extends SlingoModel {
    constructor() {
        super(...arguments);
        this.pickTaps = 0;
        this.pickTapsRequired = 0;
        this.bigWinThresholds = [];
    }
    reset() {
        super.reset();
        this.matchedSymbolColumns = [];
        this.pickTaps = 0;
    }
    getSpinResult() {
        return this.spinResult;
    }
    updateMatchedSymbolsFromDarkenedReels() {
        const indices = this.getReelDisplayData()?.getDarkenedReels() ?? [];
        this.matchedSymbolColumns = [];
        for (const index of indices) {
            this.matchedSymbolColumns.push(index);
        }
    }
    addMatchedSymbolFromDab(cell) {
        const symbols = this.getReelDisplayData().getDisplaySymbols();
        const column = cell.getColumn();
        const columnSymbol = symbols[column];
        if (cell.getNumber() === parseInt(symbols[column].getId(), 10)) {
            this.matchedSymbolColumns.push(column);
        }
        else if (columnSymbol?.isJoker() && !this.matchedSymbolColumns.includes(column)) {
            this.matchedSymbolColumns.push(column);
        }
        else {
            const superJokerIndex = symbols.findIndex((symbol, i) => symbol.isSuperJoker() && !this.matchedSymbolColumns.includes(i));
            this.matchedSymbolColumns.push(superJokerIndex);
        }
    }
    getLatestMatchedSymbol() {
        return this.spinResult?.getReelSequence()?.getSymbols()?.[this.getLatestMatchedSymbolColumn()] ?? null;
    }
    getLatestMatchedSymbolColumn() {
        return this.matchedSymbolColumns[this.matchedSymbolColumns.length - 1];
    }
    columnSymbolMatched(column) {
        return this.matchedSymbolColumns.includes(column);
    }
    columnFullyDabbed(column) {
        return this.getTicket().getRemainingCells().every(cell => cell.getColumn() !== column);
    }
    getCollectWinnings() {
        return this.spinResult?.getCollectWinnings();
    }
    getPickTapsRequired() {
        return this.pickTapsRequired;
    }
    isWildWinLine(stopIndex) {
        const winLine = this.getWinLine();
        const symbols = this.getWinLineSymbols(winLine);
        for (let i = 0; i < stopIndex; i++) {
            if (!symbols[i].isJoker() && !symbols[i].isSuperJoker()) {
                return false;
            }
        }
        return true;
    }
    getPickTaps() {
        return this.pickTaps;
    }
    getPickTapsMax() {
        // Decided by producer.
        return 7;
    }
    initialisePickTaps() {
        this.pickTaps = 0;
        // Minimum taps is at least one more than the number of scatters, required is decided by how far the multiplier is towards the maximum in the prize multiplier list.
        const minTaps = this.scatterCount + 1;
        const maxTaps = this.getPickTapsMax();
        const configId = this.getChanceGame().getGameConfig().getId();
        const possibleMultipliers = this.featureConfig.getChanceGameFeatureConfig().getMultipliers(configId);
        const wonMultiplier = this.spinResult.getChanceGame()?.getParams().getMultiplier() ?? 0;
        const wonMultiplierIndex = possibleMultipliers.indexOf(wonMultiplier);
        this.pickTapsRequired = minTaps + Math.floor((maxTaps - minTaps) * (wonMultiplierIndex / (possibleMultipliers.length - 1)));
    }
    incrementPickTaps() {
        ++this.pickTaps;
    }
    getPrizeConfig() {
        return this.currentGame.getConfig().getPrizeConfig();
    }
    getFeatureConfig() {
        return this.featureConfig;
    }
    setFeatureConfig(featureConfig) {
        this.featureConfig = featureConfig;
    }
    getWinLine() {
        return this.spinResult?.getWinLine() ?? null;
    }
    getWinLineSymbols(winLine) {
        const symbols = this.spinResult?.getReelSequence()?.getSymbols() ?? [];
        const numberOfSymbols = winLine?.getNumberOfSymbols() ?? 0;
        return symbols.slice(0, numberOfSymbols);
    }
    hasWinLineSuspense(column, winLine) {
        // Has suspense if this column might extend the number of symbols beyond the threshold of its symbol count.
        const threshold = this.featureConfig.getWinLineFeatureConfig().getThreshold(winLine.getSymbolType());
        return column > threshold && column <= winLine.getNumberOfSymbols();
    }
    getChanceGame() {
        return this.spinResult?.getChanceGame();
    }
    getSymbolHint() {
        return this.spinResult?.getSymbolHint();
    }
    setBigWinThreshold(value, level = 0) {
        this.bigWinThresholds[level] = value;
        if (level === 0) {
            super.setBigWinThreshold(value);
        }
    }
    getBigWinThreshold(level = 0) {
        return this.bigWinThresholds[level];
    }
    getBigWinThresholds() {
        return this.bigWinThresholds;
    }
    getBigWinLevel(value) {
        let index = -1;
        for (const threshold of this.bigWinThresholds) {
            if (value >= threshold) {
                ++index;
            }
            else {
                break;
            }
        }
        return index;
    }
    async waitForTicketDabsInProgress() {
        await Promise.all(this.ticketDabsInProgress.map((symbol) => symbol.waitForDabInProgress()));
    }
}
//# sourceMappingURL=GameModel.js.map